import {Button, Tooltip} from '@mui/material';
import React, {useContext} from 'react';
import dayjs from 'dayjs';
import ZeiterfassungDashboardContext, {IArbeitszeit, IUser,} from '../../../../../Contexts/ZeiterfassungDashboardContext/ZeiterfassungDashboardContext';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import jsPDF from 'jspdf';
import {useTranslation} from 'react-i18next';

export default function ExportWorkingDataPdf() {
    const {selectedDateStart, selectedDateEnde, filter, filteredData} =
        useContext(ZeiterfassungDashboardContext);
    const {t} = useTranslation();

    const isUserSelected = filter.user && filter.user !== '';

    const getUserInfo = (): IUser | null => {
        if (!filter.user || !filteredData.user_zeiterfassung) {
            return null;
        }

        return filteredData.user_zeiterfassung.find(
            (user: IUser) => user.user_id === filter.user
        ) || null;
    };

    const getUserWorkingTimes = (): IArbeitszeit[] => {
        if (!filter.user || !filteredData.arbeitszeiten) {
            return [];
        }

        return filteredData.arbeitszeiten
            .filter((entry: IArbeitszeit) => entry.user_id === filter.user)
            .sort((a, b) => new Date(a.DATUM).getTime() - new Date(b.DATUM).getTime());
    };

    const formatTimeFromDecimalHours = (decimalHours: number): string => {
        const hours = Math.floor(decimalHours);
        const minutes = Math.round((decimalHours - hours) * 60);
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    };

    const formatTimeFromMinutes = (minutes: number): string => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    };

    const formatPauseTime = (pause: number | undefined): string => {
        if (!pause || pause === 0) return '00:00';

        // Wenn Pause größer als 24 ist, dann sind es wahrscheinlich Minuten
        if (pause > 24) {
            const hours = Math.floor(pause / 60);
            const minutes = pause % 60;
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        } else {
            // Wenn Pause kleiner als 24 ist, dann sind es wahrscheinlich Dezimalstunden
            const hours = Math.floor(pause);
            const minutes = Math.round((pause - hours) * 60);
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        }
    };

    const formatTime = (timeString: string): string => {
        if (!timeString) return '';
        if (timeString.includes(':')) return timeString;
        if (timeString.length === 4) {
            return `${timeString.substring(0, 2)}:${timeString.substring(2)}`;
        }
        return timeString;
    };

    const formatDate = (dateString: string): string => {
        if (!dateString) return '';

        console.log('Original dateString:', dateString); // Debug log

        try {
            // Wenn bereits im DD.MM.YYYY Format, direkt zurückgeben
            if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateString)) {
                return dateString;
            }

            // Wenn im ISO Format (YYYY-MM-DD), zu DD.MM.YYYY konvertieren
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
                const [year, month, day] = dateString.split('-');
                return `${day}.${month}.${year}`;
            }

            // Wenn im amerikanischen Format (MM/DD/YYYY), zu DD.MM.YYYY konvertieren
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
                const [month, day, year] = dateString.split('/');
                return `${day}.${month}.${year}`;
            }

            // Versuche als Date zu parsen und dann zu formatieren
            const date = new Date(dateString);
            if (!isNaN(date.getTime())) {
                const day = date.getDate().toString().padStart(2, '0');
                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                const year = date.getFullYear();
                return `${day}.${month}.${year}`;
            }

            // Falls alles fehlschlägt, originalen String zurückgeben
            console.warn('Could not format date:', dateString);
            return dateString;
        } catch (error) {
            console.error('Date formatting error:', error, 'for:', dateString);
            return dateString;
        }
    };

    const tryLoadLogoImage = async (): Promise<{ dataUrl: string, format: string } | null> => {
        try {
            const logoUrl = '/logo.svg';

            const response = await fetch(logoUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'image/svg+xml',
                }
            });

            if (response.ok) {
                const svgText = await response.text();

                return new Promise((resolve) => {
                    const img = new Image();
                    const svgBlob = new Blob([svgText], {type: 'image/svg+xml'});
                    const url = URL.createObjectURL(svgBlob);

                    img.onload = () => {
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        if (!ctx) {
                            URL.revokeObjectURL(url);
                            resolve(null);
                            return;
                        }

                        canvas.width = 100;
                        canvas.height = 100;
                        ctx.drawImage(img, 0, 0, 100, 100);

                        const dataUrl = canvas.toDataURL('image/png');
                        URL.revokeObjectURL(url);

                        resolve({
                            dataUrl,
                            format: 'PNG'
                        });
                    };

                    img.onerror = () => {
                        URL.revokeObjectURL(url);
                        resolve(null);
                    };

                    img.src = url;
                });
            }

            return null;
        } catch (error) {
            console.warn('Could not load logo:', error);
            return null;
        }
    };

    const addSection = (doc: jsPDF, title: string, y: number): number => {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(title, 20, y);
        doc.setLineWidth(0.5);
        doc.setDrawColor(0, 0, 0);
        doc.line(20, y + 2, 190, y + 2);
        return y + 8;
    };

    const addKeyValue = (doc: jsPDF, key: string, value: string, x: number, y: number, maxWidth: number = 170): number => {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(10);

        const keyText = `${key}:`;
        const keyWidth = doc.getTextWidth(keyText) + 5;

        doc.text(keyText, x, y);

        doc.setFont('helvetica', 'normal');
        doc.setTextColor(60, 60, 60);

        const availableWidth = maxWidth - keyWidth;
        const lines = doc.splitTextToSize(value || '-', availableWidth);
        doc.text(lines, x + keyWidth, y);

        return y + (lines.length * 5);
    };

    const addFooter = (doc: jsPDF, pageNumber: number, totalPages: number) => {
        const pageHeight = doc.internal.pageSize.height;

        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(120, 120, 120);
        // Nur Seitenzahl in der Fußzeile
        doc.text(`Seite ${pageNumber} von ${totalPages}`, 170, pageHeight - 20);
    };

    const handlePdfExport = async () => {
        const userInfo = getUserInfo();
        const workingTimes = getUserWorkingTimes().sort((a, b) => {
            const [dA, mA, yA] = a.DATUM.split('.');
            const [dB, mB, yB] = b.DATUM.split('.');
            return (yA + mA + dA).localeCompare(yB + mB + dB);
        });

        console.log(workingTimes)
        console.log(workingTimes)
        console.log(workingTimes)
        console.log(workingTimes)

        if (!userInfo || workingTimes.length === 0) {
            return;
        }

        const doc = new jsPDF();
        let currentY = 30;

        doc.setFontSize(24);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Arbeitszeit-Bericht', 20, 25);

        try {
            const logoResult = await tryLoadLogoImage();
            if (logoResult) {
                doc.addImage(logoResult.dataUrl, logoResult.format, 170, 10, 20, 20);
            }
        } catch (error) {
            console.warn('Could not load logo:', error);
        }

        currentY = 50;

        currentY = addSection(doc, 'Berichtszeitraum', currentY);
        const startDate = dayjs(selectedDateStart).format('DD.MM.YYYY');
        const endDate = dayjs(selectedDateEnde).format('DD.MM.YYYY');
        currentY = addKeyValue(doc, 'Zeitraum', `${startDate} - ${endDate}`, 20, currentY);
        currentY += 5;

        currentY = addSection(doc, 'Mitarbeiter Informationen', currentY);
        currentY = addKeyValue(doc, 'Name', userInfo.fullname || userInfo.email || '', 20, currentY);
        // Email entfernt - nur noch Name wird angezeigt
        if (userInfo.department) {
            currentY = addKeyValue(doc, 'Abteilung', userInfo.department, 20, currentY);
        }
        currentY += 10;

        currentY = addSection(doc, 'ARBEITSZEITEN', currentY);
        currentY += 5;

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);

        const colWidths = {
            datum: 32,
            von: 22,
            bis: 22,
            pause: 30, // Etwas mehr Platz für bessere Formatierung der Pause
            projekt: 48,
            gesamtzeit: 30
        };

        let xPos = 20;
        doc.text('Datum', xPos, currentY);
        xPos += colWidths.datum;
        doc.text('Von', xPos, currentY);
        xPos += colWidths.von;
        doc.text('Bis', xPos, currentY);
        xPos += colWidths.bis;
        doc.text('Pause', xPos, currentY);
        xPos += colWidths.pause;
        doc.text('Projekt', xPos, currentY);
        xPos += colWidths.projekt;
        doc.text('Gesamtzeit', xPos, currentY);
        currentY += 5;

        doc.line(20, currentY, 190, currentY);
        currentY += 8;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        let totalDecimalHours = 0;

        workingTimes.forEach((entry) => {
            if (currentY > 270) {
                addFooter(doc, 1, 2);
                doc.addPage();
                currentY = 20;
            }

            xPos = 20;

            doc.text(formatDate(entry.DATUM), xPos, currentY);
            xPos += colWidths.datum;

            doc.text(formatTime(entry.BEGINN), xPos, currentY);
            xPos += colWidths.von;

            doc.text(formatTime(entry.ENDE), xPos, currentY);
            xPos += colWidths.bis;

            const pauseText = formatPauseTime(entry.PAUSE);
            doc.text(pauseText, xPos, currentY);
            xPos += colWidths.pause;

            const projektText = entry.BVH || entry.TAETIGKEIT || '';
            const displayProject = projektText.length > 25 ? projektText.substring(0, 25) + '...' : projektText;
            doc.text(displayProject, xPos, currentY);
            xPos += colWidths.projekt;

            let totalTime = '';
            if (entry.DAUER !== undefined && entry.DAUER !== null) {
                if (entry.DAUER > 24) {
                    totalTime = formatTimeFromMinutes(entry.DAUER);
                    totalDecimalHours += entry.DAUER / 60;
                } else {
                    totalTime = formatTimeFromDecimalHours(entry.DAUER);
                    totalDecimalHours += entry.DAUER;
                }
            } else {
                totalTime = '00:00';
            }
            doc.text(totalTime, xPos, currentY);

            currentY += 6;
        });

        // Zusammenfassung
        currentY += 5;
        doc.line(20, currentY, 190, currentY);
        currentY += 10;

        currentY = addSection(doc, 'ZUSAMMENFASSUNG', currentY);
        const totalFormatted = formatTimeFromDecimalHours(totalDecimalHours);
        // Nur Zeit in Stunden anzeigen, Tage-Anzeige entfernt
        currentY = addKeyValue(doc, 'Gesamtarbeitszeit', totalFormatted, 20, currentY);

        addFooter(doc, 1, 1);

        const today = dayjs().format('YYYY-MM-DD');
        const sanitizedName = (userInfo.fullname || userInfo.email || 'Unbekannt').replace(/[^a-zA-Z0-9äöüÄÖÜß\\s]/g, '').replace(/\\s+/g, '_');
        const filename = `Arbeitszeit_${sanitizedName}_${startDate.replace(/\\./g, '-')}_${endDate.replace(/\\./g, '-')}_${today}.pdf`;

        doc.save(filename);
    };

    const getTooltipText = () => {
        if (!isUserSelected) {
            return 'PDF Export - Bitte wählen Sie einen Mitarbeiter aus';
        }
        const userInfo = getUserInfo();
        const workingTimes = getUserWorkingTimes();

        if (!userInfo) {
            return 'Benutzerinformationen nicht gefunden';
        }

        if (workingTimes.length === 0) {
            return 'Keine Arbeitszeiten gefunden';
        }

        return 'PDF Export - Arbeitszeiten als PDF herunterladen';
    };

    const userInfo = getUserInfo();
    const workingTimes = getUserWorkingTimes();
    const canExport = isUserSelected && userInfo && workingTimes.length > 0;

    console.log('PDF Export Debug:', {
        filterUser: filter.user,
        isUserSelected,
        userInfo: !!userInfo,
        workingTimesLength: workingTimes.length,
        canExport
    }); // Debug log

    return (
        <Tooltip title={getTooltipText()}>
      <span>
        <Button
            id="pdf-export-button"
            variant="contained"
            onClick={canExport ? handlePdfExport : undefined}
            disabled={!canExport}
            startIcon={<PictureAsPdfIcon/>}
            sx={{
                backgroundColor: canExport ? '#d32f2f' : '#e0e0e0',
                color: canExport ? 'white' : '#9e9e9e',
                fontWeight: 'bold',
                textTransform: 'none',
                minWidth: '120px',
                '&:hover': {
                    backgroundColor: canExport ? '#c62828' : '#e0e0e0',
                    boxShadow: canExport ? '0 4px 12px rgba(211, 47, 47, 0.4)' : 'none',
                },
                '&:active': {
                    backgroundColor: canExport ? '#b71c1c' : '#e0e0e0',
                },
                '&.Mui-disabled': {
                    backgroundColor: '#e0e0e0 !important',
                    color: '#9e9e9e !important',
                    cursor: 'not-allowed',
                },
                boxShadow: canExport ? '0 2px 8px rgba(211, 47, 47, 0.3)' : 'none',
                cursor: canExport ? 'pointer' : 'not-allowed',
            }}
        >
          PDF Export
        </Button>
      </span>
        </Tooltip>
    );
}
